import React, { useState, useMemo, forwardRef } from "react";
import { Table, Container } from "reactstrap";
import { Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Search01Icon,
  FilterIcon,
  TableIcon,
} from "@hugeicons/core-free-icons";
import { Badge, Flex } from "antd";
import { useTableColumns } from "../../utils/hooks/useTableColumns";
import { ColumnVisibilityDropdown } from "../ColumnVisibilityDropdown";
import { useScreenSize } from "../../utils/hooks/useScreenSize ";
import ResponsiveCard from "../ResponsiveCard";
import ActionDropdown from "../ActionDropdown";
import { AntInput } from "../AntInput";
import BtnAsLink from "../../Component/BtnAsLink";
import { GenericFilterModal } from "./GenericFilterModal";
import { useCustomContext } from "../../_helper/customizer";
import { AntSekelton } from "@/Component/Skeleton";
import { AntPagination } from "@/Component/AntPagination/AntPagination";
/**
 * TableC - A comprehensive, responsive table component with built-in features
 * including search, filtering, pagination, column visibility, and actions.
 *
 * Automatically converts to card view on mobile/tablet devices when responsive=true.
 *
 * @component
 * @example
 * // Basic usage with columns and data
 * const columns = [
 *   { name: t("NAME"), key: "name", filterable: true },
 *   { name: t("EMAIL"), key: "email", filterable: true },
 *   { name: t("ACTIONS"), key: "actions", filterable: false }
 * ];
 *
 * <TableC
 *   data={users}
 *   columns={columns}
 *   loading={isLoading}
 * />
 *
 * @example
 * // With pagination
 * <TableC
 *   data={users}
 *   columns={columns}
 *   pagination={{
 *     current: 1,
 *     total: 100,
 *     pageSize: 10,
 *     onChange: (page) => setCurrentPage(page)
 *   }}
 * />
 *
 * @example
 * // With search and actions
 * const getActions = (rowData) => [
 *   {
 *     key: "edit",
 *     label: <Link to={`/edit/${rowData.id}`}>{t("EDIT")}</Link>
 *   },
 *   {
 *     key: "delete",
 *     label: t("DELETE"),
 *     onClick: () => handleDelete(rowData.id),
 *     danger: true
 *   }
 * ];
 *
 * <TableC
 *   data={users}
 *   columns={columns}
 *   searchValue={searchTerm}
 *   onSearchChange={(e) => setSearchTerm(e.target.value)}
 *   searchPlaceholder={t("SEARCH_USERS")}
 *   actions={getActions}
 *   renderActions={<Button>Create</Button>}
 * />
 *
 * @example
 * // With custom column rendering
 * const columns = [
 *   {
 *     name: t("DATE"),
 *     key: "created_at",
 *     filterable: true,
 *     render: (value) => formatDate(value)
 *   },
 *   {
 *     name: t("STATUS"),
 *     key: "status",
 *     filterable: true,
 *     render: (value, rowData) => (
 *       <Badge color={value === "active" ? "success" : "danger"}>
 *         {value}
 *       </Badge>
 *     )
 *   }
 * ];
 *
 * @example
 * // With filters
 * const filterFields = [
 *   {
 *     name: "status",
 *     label: t("STATUS"),
 *     type: "select",
 *     options: [
 *       { label: t("ACTIVE"), value: "active" },
 *       { label: t("INACTIVE"), value: "inactive" }
 *     ]
 *   },
 *   {
 *     name: "date_from",
 *     label: t("FROM_DATE"),
 *     type: "input",
 *     inputType: "date"
 *   }
 * ];
 *
 * <TableC
 *   data={users}
 *   columns={columns}
 *   filterFields={filterFields}
 *   filters={appliedFilters}
 *   onFilterApply={(filters) => setAppliedFilters(filters)}
 *   onFilterReset={() => setAppliedFilters({})}
 * />
 *
 * @param {Array<Object>} [data=[]] - Array of row data objects. Each object should have properties matching column keys.
 * @param {Array<Column>} [columns=[]] - Column configuration array. See Column type below.
 * @param {boolean} [loading=false] - Shows skeleton loading state when true.
 * @param {string} [searchValue=""] - Current search input value.
 * @param {boolean} [columnsFilter=true] - Show/hide column visibility dropdown.
 * @param {Function} [onSearchChange] - Callback fired when search input changes. Receives event: `(e) => void`
 * @param {string} [searchPlaceholder=""] - Placeholder text for search input.
 * @param {Object} [pagination] - Pagination configuration object.
 * @param {number} pagination.current - Current page number (1-indexed).
 * @param {number} pagination.total - Total number of items.
 * @param {number} pagination.pageSize - Number of items per page.
 * @param {Function} pagination.onChange - Callback fired on page change: `(page) => void`
 * @param {Array<Action>|Function} [actions] - Row actions. Can be array or function that receives rowData and returns array.
 * @param {Array<FilterField>} [filterFields=[]] - Filter field configurations for generic filter modal.
 * @param {React.Component} [filterModal] - Custom filter modal component. Overrides GenericFilterModal.
 * @param {Object} [filters={}] - Current applied filter values (key-value pairs).
 * @param {Function} [onFilterApply] - Callback fired when filters are applied: `(filterData) => void`
 * @param {Function} [onFilterReset] - Callback fired when filters are reset: `() => void`
 * @param {React.ReactNode} [renderActions] - Custom action buttons/elements rendered in header section.
 * @param {React.Component} [emptyState] - Custom empty state component. Receives `{ hasSearch: boolean }` prop.
 * @param {string} [headerKey] - Key from rowData to use as header in responsive card view.
 * @param {Function} [headerRender] - Custom header render function for responsive cards: `(rowData) => ReactNode`
 * @param {number} [minVisibleColumns=2] - Minimum number of columns that must remain visible.
 * @param {number} [skeletonCount=3] - Number of skeleton rows to show during loading.
 * @param {string} [emptyStateTitle] - Custom title for empty state (no data).
 * @param {string} [emptyStateDescription] - Custom description for empty state (no data).
 * @param {string} [emptyStateNoResultsTitle] - Custom title for empty state when search returns no results.
 * @param {string} [emptyStateNoResultsDescription] - Custom description for empty state when search returns no results.
 * @param {boolean} [responsive=true] - Enable responsive behavior (converts to cards on mobile/tablet).
 *
 * @typedef {Object} Column - Column configuration object
 * @property {string} name - Column header display name (usually translated).
 * @property {string} key - Property key from data object to display.
 * @property {boolean} [filterable=true] - Whether column can be shown/hidden via column filter.
 * @property {boolean} [visible=true] - Initial visibility state (for non-filterable columns).
 * @property {Function} [render] - Custom render function: `(value, rowData) => ReactNode`. If not provided, displays raw value or "-" if null/undefined.
 * @property {number|Object} [colSize] - Column size for responsive card view (passed to ResponsiveCard).
 * @property {string} [dir] - Text direction for column content ("ltr" | "rtl").
 *
 * @typedef {Object} Action - Row action configuration
 * @property {string} key - Unique action identifier.
 * @property {React.ReactNode} label - Action label/content to display.
 * @property {Function} [onClick] - Callback fired when action is clicked: `() => void`
 * @property {boolean} [danger] - Apply danger styling to action item.
 * @property {string} [type] - Action type. Use "divider" for separator.
 *
 * @typedef {Object} FilterField - Filter field configuration
 * @property {string} name - Field name (used as filter key).
 * @property {string|Function} label - Field label (can be function for translation).
 * @property {string} [type="input"] - Field type: "input" | "select".
 * @property {string} [inputType="text"] - Input type when type is "input": "text" | "date" | "number" etc.
 * @property {string|Function} [placeholder] - Placeholder text (can be function for translation).
 * @property {Array} [options] - Options array for select type. Format: `[{ label, value }, ...]`
 * @property {*} [defaultValue] - Default field value.
 * @property {Object} [colSize] - Column size for field in modal grid: `{ md: 6 }` etc.
 *
 * @returns {JSX.Element} The rendered table component
 */
export const TableC = forwardRef(
  (
    {
      data = [],
      columns = [],
      loading = false,
      searchValue = "",
      columnsFilter = true,
      onSearchChange,
      searchPlaceholder = "",
      pagination,
      actions,
      filterFields = [],
      filterModal: CustomFilterModal,
      filters = {},
      onFilterApply,
      onFilterReset,
      renderActions,
      renderPostfixActions,
      emptyState: CustomEmptyState,
      headerKey,
      headerRender,
      minVisibleColumns = 2,
      skeletonCount = 3,
      emptyStateTitle,
      emptyStateDescription,
      emptyStateNoResultsTitle,
      emptyStateNoResultsDescription,
      responsive = true,
      getRowStyle,
      rowKey,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const { isMobile, isTablet } = useScreenSize();
    const [showFilterModal, setShowFilterModal] = useState(false);
    const { layout } = useCustomContext();
    // Use the custom hook for column visibility
    const tableColumns = useTableColumns(columns, { minVisibleColumns });

    const toggleFilterModal = () => {
      setShowFilterModal(!showFilterModal);
    };

    const handleFilterApply = (filterData) => {
      if (onFilterApply) {
        onFilterApply(filterData);
      }
    };

    const handleFilterReset = () => {
      if (onFilterReset) {
        onFilterReset();
      }
    };

    // Convert columns to card format for ResponsiveCard
    const cardColumns = useMemo(() => {
      return columns
        ?.filter?.((col) => col.key !== "actions")
        .map((col) => ({
          key: col?.key,
          name: col?.name,
          render: col?.render,
          colSize: col?.colSize,
          dir: col?.dir,
        }));
    }, [columns]);

    // Get actions for a row
    const getRowActions = (rowData, index) => {
      if (!actions) return [];
      if (typeof actions === "function") {
        return actions(rowData, index);
      }
      return actions;
    };

    const getRowKey = (rowData, index) => {
      if (typeof rowKey === "function") {
        return rowKey(rowData, index);
      }

      if (typeof rowKey === "string" && rowData?.[rowKey] != null) {
        return rowData[rowKey];
      }

      return index;
    };

    // Render table row
    const renderTableRow = (rowData, index) => {
      const rowActions = getRowActions(rowData, index);
      const visibleKeys = tableColumns.visibleColumnsList.map((col) => col.key);
      const hasActionsColumn = visibleKeys.includes("actions");

      // Get row style if getRowStyle function is provided
      const rowStyle = getRowStyle ? getRowStyle(rowData, index) : {};

      return (
        <tr key={getRowKey(rowData, index)} style={rowStyle}>
          {tableColumns.visibleColumnsList.map((column) => {
            // Handle actions column separately
            if (column.key === "actions") {
              if (!hasActionsColumn) return null;
              // Show actions column even if no actions for this row (empty cell)
              if (rowActions.length === 0) {
                return <td key="actions">-</td>;
              }

              // Multiple actions: use kebab menu
              if (rowActions.length > 1) {
                return (
                  <td key="actions">
                    <ActionDropdown
                      removeContainer={true}
                      disabled={rowActions
                        .map((action) => action?.disabled)
                        .every((disabled) => disabled)}
                      items={rowActions}
                      buttonClassName="outline btn-icon btn-xs"
                    />
                  </td>
                );
              }

              // Single action: the menu path above wires onClick via antd; rendering only
              // `label` drops onClick, so actions like Print become inert text.
              const single = rowActions[0];
              if (single?.onClick) {
                return (
                  <td key="actions" className="text-nowrap">
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-decoration-none d-inline-flex align-items-center gap-1 border-0 bg-transparent"
                      onClick={(e) => {
                        e.stopPropagation();
                        single.onClick(e);
                      }}
                      disabled={single.disabled}
                      style={{
                        backgroundColor: "transparent",
                      }}
                    >
                      {single.label}
                    </button>
                  </td>
                );
              }
              return <td key="actions">{single?.label}</td>;
            }

            // Get the value for this column
            const value = rowData[column.key];

            // Use custom render function if provided
            const renderedValue = column.render ? (
              column.render(value, rowData, index)
            ) : (
              value || "-"
            );

            return <td key={column.key}>{renderedValue}</td>;
          })}
        </tr>
      );
    };

    // Render skeleton table
    const renderSkeletonTable = () => (
      <Table responsive hover>
        <thead>
          <tr>
            {tableColumns.visibleColumnsList?.map?.((column) => (
              <th key={column.key}>{column.name}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <tr key={`skeleton-${index}`}>
              {tableColumns.visibleColumnsList?.map?.((column) => (
                <td key={column.key}>
                  <AntSekelton.Text />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    );

    // Render skeleton cards
    const renderSkeletonCards = () => (
      <div>
        {Array.from({ length: skeletonCount }).map((_, index) => (
          <ResponsiveCard
            key={`skeleton-${index}`}
            data={{}}
            columns={cardColumns}
            visibleColumnsList={tableColumns.visibleColumnsList}
            isLoading={true}
            skeletonCount={6}
          />
        ))}
      </div>
    );

    // Render empty state
    const renderEmptyState = (hasSearch = false) => {
      if (CustomEmptyState) {
        return <CustomEmptyState hasSearch={hasSearch} />;
      }

      return (
        <div className="text-center py-5">
          <div className="mb-3" style={{ fontSize: "3rem", opacity: 0.3 }}>
            <HugeiconsIcon size={40} icon={TableIcon} />
          </div>
          <h4>
            {hasSearch
              ? emptyStateNoResultsTitle || t("NO_RESULTS")
              : emptyStateTitle || t("NO_DATA")}
          </h4>
          <p className="text-muted">
            {hasSearch
              ? emptyStateNoResultsDescription || t("NO_RESULTS_DESCRIPTION")
              : emptyStateDescription || t("NO_DATA_DESCRIPTION")}
          </p>
        </div>
      );
    };

    const hasSearchValue = searchValue && searchValue?.trim?.() !== "";
    const showEmptyState = !loading && data.length === 0;
    const isResponsive = (isMobile || isTablet) && responsive;
    const showSearchSection =
      searchPlaceholder ||
      onSearchChange ||
      renderPostfixActions ||
      renderActions ||
      filterFields.length > 0 ||
      CustomFilterModal ||
      columns?.length > 0;

    return (
      <Stack gap={3} ref={ref}>
        {/* Search and Actions Section */}
        {showSearchSection && (
          <Container fluid className="px-0">
            <Stack direction="horizontal" gap={2} className="w-100">
              {(searchPlaceholder || onSearchChange) && (
                <div className="flex-fill">
                  <AntInput
                    prefix={<HugeiconsIcon icon={Search01Icon} />}
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchValue || ""}
                    onChange={onSearchChange}
                  />
                </div>
              )}
              <Flex
                gap={8}
                className={layout === "rtl" ? "me-auto" : "ms-auto"}
              >
                {/* Custom render actions */}
                {renderPostfixActions && <div>{renderPostfixActions}</div>}
                {/* Filter Button */}
                {(filterFields.length > 0 || CustomFilterModal) && (
                  <Badge
                    count={
                      Object.values(filters || {})?.filter?.(Boolean)?.length
                    }
                  >
                    <BtnAsLink
                      className="btn-secondary"
                      icon={<HugeiconsIcon icon={FilterIcon} />}
                      onClick={toggleFilterModal}
                      disabled={loading}
                    >
                      {t("FILTER")}
                    </BtnAsLink>
                  </Badge>
                )}
                {/* Column visibility dropdown */}
                {columnsFilter && (
                  <ColumnVisibilityDropdown
                    {...tableColumns}
                    disabled={loading}
                  />
                )}
                {/* Custom render actions */}
                {renderActions && <div>{renderActions}</div>}
              </Flex>
            </Stack>
          </Container>
        )}

        {/* Table/Cards Section */}
        {loading ? (
          isResponsive ? (
            renderSkeletonCards()
          ) : (
            renderSkeletonTable()
          )
        ) : showEmptyState ? (
          renderEmptyState(hasSearchValue)
        ) : isResponsive ? (
          <div>
            {data.map((rowData, index) => {
              const rowActions = getRowActions(rowData, index);
              const rowStyle = getRowStyle ? getRowStyle(rowData, index) : {};
              return (
                <ResponsiveCard
                  key={getRowKey(rowData, index)}
                  index={index}
                  data={rowData}
                  columns={cardColumns}
                  actions={rowActions}
                  headerKey={headerKey}
                  headerRender={headerRender}
                  visibleColumnsList={tableColumns.visibleColumnsList}
                  style={rowStyle}
                />
              );
            })}
          </div>
        ) : (
          <Table responsive hover>
            <thead>
              <tr>
                {tableColumns.visibleColumnsList.map((column) => (
                  <th key={column.key}>{column.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data?.map?.((rowData, index) => renderTableRow(rowData, index))}
            </tbody>
          </Table>
        )}

        {/* Pagination */}
        {pagination?.total > 0 && <AntPagination
          className="w-100 p-3 bg-white position-sticky bottom-0"
          // hideOnSinglePage
          showSizeChanger={false}
          {...(pagination || {})}
        />}

        {/* Filter Modal */}
        {CustomFilterModal ? (
          <CustomFilterModal
            isOpen={showFilterModal}
            toggle={toggleFilterModal}
            onApply={handleFilterApply}
            filters={filters}
          />
        ) : filterFields.length > 0 ? (
          <GenericFilterModal
            isOpen={showFilterModal}
            toggle={toggleFilterModal}
            onApply={handleFilterApply}
            onFilterReset={handleFilterReset}
            filters={filters}
            filterFields={filterFields}
          />
        ) : null}
      </Stack>
    );
  }
);

export default TableC;
